/* eslint-disable import/no-default-export */
import React from 'react';
import { RemoveEmails } from './RemoveEmails';
import { LoginWithAtlassian } from './LoginWithAtlassian';
import { WorkAndOther } from './WorkAndOther';
import { Success } from './Success';
import { NotWork } from './NotWork';
import { AccountType } from './AccountType';
import { usePrepMemberForAtlassianAccountOnboardingMutation } from '@trello/graphql/generated';

import { Noun, Verb } from '@trello/analytics';
import Storage from '@trello/storage';
import { track } from './Analytics';

const STORAGE_SESSION_KEY = 'meta-analytics-flags';
const STORAGE_SESSION_ITEM = 'atlassian-account-onboarding-flow';

export enum ScreenType {
  ACCOUNT_TYPE = 'accountType',
  LOGIN_WITH_ATLASSIAN = 'loginWithAtlassian',
  NOT_WORK = 'notWork',
  REMOVE_EMAILS = 'removeEmails',
  SUCCESS = 'success',
  WORK_AND_OTHER = 'workAndOther',
}

interface Login {
  id: string;
  email: string;
}

interface AtlassianAccountOnboardingOverlayProps {
  analyticsContext: object;
  screen: ScreenType;
  aaEmail: string;
  logins: Login[];
  orgName: string;
  dismiss: () => void;
  setScreen: (screenType: ScreenType) => void;
  images: Map<ScreenType, HTMLImageElement>;
}

export const AtlassianAccountOnboardingOverlay: React.FunctionComponent<
  AtlassianAccountOnboardingOverlayProps
> = ({
  analyticsContext,
  screen,
  aaEmail,
  logins,
  orgName,
  dismiss,
  setScreen,
  images,
}) => {
  const [removeEmails] = usePrepMemberForAtlassianAccountOnboardingMutation();

  const aaLogin = logins.find(({ email }) => email === aaEmail);
  const aaLoginId = aaLogin && aaLogin.id;
  const otherLogins = logins.filter(({ email }) => email !== aaEmail);
  const nonAaLoginIds = otherLogins.map((login) => login.id);
  const otherEmails = otherLogins.map((login) => login.email);

  if (!aaLoginId) {
    return null;
  }

  //Set flag in local storage for analytics in meta repo
  if (!Storage.get(STORAGE_SESSION_KEY)) {
    Storage.set(STORAGE_SESSION_KEY, { referrer: STORAGE_SESSION_ITEM });
  }

  let screenToDisplay = screen;
  // If the user has no `otherEmails` then skip the REMOVE_EMAILS screen
  if (
    screenToDisplay === ScreenType.REMOVE_EMAILS &&
    otherEmails.length === 0
  ) {
    screenToDisplay = ScreenType.LOGIN_WITH_ATLASSIAN;
  }

  switch (screenToDisplay) {
    case ScreenType.REMOVE_EMAILS:
      return (
        <RemoveEmails
          image={images.get(ScreenType.REMOVE_EMAILS)}
          onDismiss={dismiss}
          aaEmail={aaEmail}
          otherEmails={otherEmails}
          orgName={orgName}
          onClickRemoveEmails={() => {
            removeEmails({
              variables: {
                memberId: 'me',
                aaLoginId,
                nonAaLoginIds,
              },
            });

            track(
              Verb.REMOVES,
              Noun.EMAIL,
              'via onboarding modal',
              analyticsContext,
            );

            setScreen(ScreenType.LOGIN_WITH_ATLASSIAN);
          }}
        />
      );
    case ScreenType.LOGIN_WITH_ATLASSIAN:
      return (
        <LoginWithAtlassian
          image={images.get(ScreenType.LOGIN_WITH_ATLASSIAN)}
          onClick={() => {
            track(
              Verb.COMPLETES,
              Noun.ONBOARDING_MENU,
              'by logging into atlassian account',
              analyticsContext,
            );
          }}
          onDismiss={dismiss}
          aaEmail={aaEmail}
          orgName={orgName}
        />
      );
    case ScreenType.WORK_AND_OTHER:
      return (
        <WorkAndOther
          image={images.get(ScreenType.WORK_AND_OTHER)}
          onDismiss={dismiss}
          orgName={orgName}
          onClickSetupAccount={() => {
            track(
              Verb.LEAVES,
              Noun.ONBOARDING_MENU,
              'by clicking the remove emails button',
              analyticsContext,
            );
            setScreen(ScreenType.REMOVE_EMAILS);
          }}
          onClickGoBack={() => {
            track(
              Verb.CHANGES,
              Noun.ONBOARDING_MENU,
              'by clicking back button',
              analyticsContext,
            );
            setScreen(ScreenType.ACCOUNT_TYPE);
          }}
        />
      );
    case ScreenType.NOT_WORK:
      return (
        <NotWork
          image={images.get(ScreenType.NOT_WORK)}
          onClickRemove={() => {
            track(
              Verb.COMPLETES,
              Noun.ONBOARDING_MENU,
              'by indicating intent to remove claimed email',
              analyticsContext,
            );
            dismiss();
          }}
          onDismiss={dismiss}
          orgName={orgName}
          onClickGoBack={() => {
            track(
              Verb.CHANGES,
              Noun.ONBOARDING_MENU,
              'by clicking back button',
              analyticsContext,
            );
            setScreen(ScreenType.ACCOUNT_TYPE);
          }}
        />
      );
    case ScreenType.SUCCESS:
      return (
        <Success
          image={images.get(ScreenType.SUCCESS)}
          onDismiss={() => {
            track(
              Verb.COMPLETES,
              Noun.ONBOARDING_SUCCESS_MODAL,
              'by dismissing final success screen',
              analyticsContext,
            );
            dismiss();
          }}
          aaEmail={aaEmail}
          orgName={orgName}
        />
      );
    case ScreenType.ACCOUNT_TYPE:
      return (
        <AccountType
          image={images.get(ScreenType.ACCOUNT_TYPE)}
          onDismiss={dismiss}
          orgName={orgName}
          onClickWorkAndOthers={() => {
            track(
              Verb.CHANGES,
              Noun.ONBOARDING_MENU,
              'by clicking the mixed use account button',
              analyticsContext,
            );
            setScreen(ScreenType.WORK_AND_OTHER);
          }}
          onClickNotWork={() => {
            track(
              Verb.CHANGES,
              Noun.ONBOARDING_MENU,
              'by clicking the personal account button',
              analyticsContext,
            );
            setScreen(ScreenType.NOT_WORK);
          }}
        />
      );
    default:
      return null;
  }
};

export default AtlassianAccountOnboardingOverlay;
